import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "../contexts/AuthContext";

/**
 * MonCarnet — protected dashboard (P1 placeholder).
 * Phase 2 will add entries CRUD here.
 */
export default function MonCarnet() {
  const { user, logout, updateMe } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", bio: user?.bio || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  if (!user) return null; // Should be wrapped in ProtectedRoute

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateMe(form);
      setSaved(true);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="mon-carnet-page" className="bg-[#FBF8EF] min-h-screen pt-28 md:pt-36 pb-32">
      <SEO title={`Mon carnet — Pulaar`} description="Votre carnet Pulaar." path="/mon-carnet" />
      <div className="mx-auto max-w-3xl px-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-4">— Bienvenue</p>
            <h1 className="font-serif text-4xl md:text-6xl text-[#121212] leading-[0.95] tracking-[-0.02em]">
              {user.name}<span className="italic font-light">.</span>
            </h1>
            <p className="mt-3 text-sm font-serif italic text-[#4A4641]">
              Votre carnet public :{" "}
              <Link to={`/journal/@${user.username}`} className="link-underline text-[#121212]" data-testid="mon-carnet-public-link">
                /journal/@{user.username}
              </Link>
            </p>
          </div>
          <button
            type="button" onClick={logout}
            data-testid="mon-carnet-logout"
            className="shrink-0 text-[10px] tracking-[0.32em] uppercase text-[#5C4033] link-underline"
          >
            Déconnexion
          </button>
        </div>

        {/* Profile editor */}
        <section className="mt-16 border-t border-[#D8D2C5] pt-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">— Profil</p>
          <form onSubmit={onSave} className="space-y-6 max-w-md">
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-2">Nom</label>
              <input
                data-testid="mon-carnet-input-name"
                type="text" maxLength={80}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-11 bg-transparent border-b border-[#D8D2C5] outline-none text-sm text-[#121212] focus:border-[#121212] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-2">Bio</label>
              <textarea
                data-testid="mon-carnet-input-bio"
                rows={3} maxLength={500}
                value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Une phrase pour situer votre carnet…"
                className="w-full bg-transparent border-b border-[#D8D2C5] outline-none text-sm text-[#121212] focus:border-[#121212] transition-colors resize-none py-2"
              />
            </div>
            {error && <p className="text-xs" style={{ color: "#A33A2A" }}>{error}</p>}
            {saved && <p className="text-xs text-[#5C4033]">Enregistré.</p>}
            <button
              type="submit" disabled={saving}
              data-testid="mon-carnet-save"
              className="h-11 px-10 text-[11px] tracking-[0.32em] uppercase bg-[#121212] text-[#FBF8EF] inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-500 hover:bg-[#3A322C]"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </section>

        {/* Coming soon */}
        <section className="mt-16 border-t border-[#D8D2C5] pt-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">— Entrées</p>
          <p className="font-serif italic text-lg text-[#4A4641] leading-relaxed max-w-lg">
            Bientôt, vous pourrez ajouter ici vos adresses, images, sons, notes et vidéos — une entrée par semaine, ou autant que vous voulez.
          </p>
        </section>
      </div>
    </main>
  );
}
